import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtEUR } from "@/lib/billing";
import { CreditCard, ExternalLink, Users, Clock, Zap, TriangleAlert, CheckCircle2, Lock, Sparkles, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState(null);
  const [period, setPeriod] = useState("month");
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const paywallHit = params.get("paywall") === "1";

  // Per-tier extra-seats picker state (handmatig bij checkout).
  // Wordt geïnitialiseerd op de al ingeplande extra werknemers (of 0 als niks is ingepland).
  const [seatChoice, setSeatChoice] = useState({ start: 0, pro: 0, max: 0 });

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get("/billing/status"), api.get("/billing/plans")]);
      setStatus(s.data);
      setPlans(p.data);
      if (s.data.period) setPeriod(s.data.period);
      // Init seat-picker per tier: use current billed seats OR planned overflow, whichever is bigger
      const planned = s.data.active_uitvoerders_this_month ?? 0;
      const init = {};
      for (const t of (p.data.tiers || [])) {
        const inc = t.included_seats || 0;
        init[t.tier] = Math.max(0, planned - inc);
      }
      setSeatChoice((sc) => ({ ...sc, ...init }));
    } catch { toast.error("Kon facturatie niet laden"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("canceled")) {
      toast.info("Afrekenen geannuleerd.");
      setParams({});
    }
  }, [params, setParams]);

  const startCheckout = async (tier, extraSeatsOverride) => {
    setBusy(true);
    try {
      const extra_seats = extraSeatsOverride ?? seatChoice[tier] ?? 0;
      const r = await api.post("/billing/checkout", { tier, period, origin_url: window.location.origin, extra_seats });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : (raw?.message || "Kon Stripe checkout niet openen. Probeer 'Betaalmethode toevoegen' hieronder.");
      toast.error(msg);
      setBusy(false);
    }
  };

  const changeTier = async (tier) => {
    if (!confirm(`Overstappen naar Ploegbord ${tier.toUpperCase()}? Verrekening (proration) via Stripe.`)) return;
    setBusy(true);
    try {
      await api.post("/billing/change-tier", { tier, period });
      toast.success("Abonnement gewijzigd");
      await load();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : (raw?.message || "Wijzigen mislukt"));
    }
    setBusy(false);
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/portal");
      window.location.href = r.data.portal_url;
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : (raw?.message || "Portal niet beschikbaar — kies eerst een pakket");
      toast.error(msg);
      setBusy(false);
    }
  };

  const syncSeats = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/sync-seats");
      toast.success(`Seats gesynchroniseerd (${r.data.seats_charged ?? 0} extra)`);
      await load();
    } catch { toast.error("Sync mislukt"); }
    setBusy(false);
  };

  const setSeat = (tier, val) => {
    const n = Math.max(0, Math.min(100, parseInt(val || 0, 10) || 0));
    setSeatChoice((s) => ({ ...s, [tier]: n }));
  };

  if (user?.role !== "OWNER") {
    return <div className="p-8 text-slate-600 font-semibold">Alleen de eigenaar heeft toegang tot facturatie.</div>;
  }
  if (!status || !plans) return <div className="p-8">Laden…</div>;

  const currentPlan = status.tier ? plans.tiers.find((t) => t.tier === status.tier) : null;
  const hasSubscription = !!status.tier;
  const trialActive = status.status === "trialing" && status.trial_ends_at && new Date(status.trial_ends_at) > new Date();
  const badStatus = ["past_due", "unpaid", "canceled", "paused"].includes(status.status || "");
  const isPaywalled = (paywallHit && !trialActive) || (!hasSubscription && !trialActive) || badStatus;
  let paywallHeadline = "Je proefperiode is voorbij";
  let paywallBody = "Kies hieronder je pakket + aantal extra werknemers. Je betaalt in één keer met iDEAL en daarna gaat de automatische SEPA-incasso lopen. Klaar in 1 minuut.";
  if (badStatus) {
    if (status.status === "past_due") { paywallHeadline = "Betaling mislukt — update je betaalmethode"; paywallBody = "Je laatste betaling kon niet worden geïncasseerd. Werk je betaalgegevens bij via Stripe en je toegang wordt direct hersteld."; }
    else if (status.status === "paused") { paywallHeadline = "Abonnement gepauzeerd"; paywallBody = "Je abonnement staat op pauze omdat er geen betaalmethode is. Voeg er nu een toe en werk direct verder."; }
    else if (status.status === "canceled") { paywallHeadline = "Abonnement opgezegd"; paywallBody = "Je abonnement is opgezegd. Kies opnieuw een pakket + aantal extra werknemers om weer te starten."; }
    else if (status.status === "unpaid") { paywallHeadline = "Openstaande factuur"; paywallBody = "Er staat nog een factuur open. Betaal die via de portal om je account te heractiveren."; }
  } else if (!hasSubscription && !trialActive) {
    paywallHeadline = "Kies je pakket + extra werknemers"; paywallBody = "Selecteer hieronder je pakket, kies hoeveel extra werknemers je nu meeneemt en betaal veilig met iDEAL. Daarna loopt de automatische incasso.";
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8" data-testid="billing-page">
      {isPaywalled && (
        <div className="mb-6 rounded-lg border-l-8 border-orange-600 bg-white p-6 md:p-8 shadow-sm" data-testid="paywall-banner">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="p-3 rounded-full bg-orange-600 text-white shrink-0"><Lock size={26}/></div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] font-black uppercase tracking-widest text-orange-700 flex items-center gap-1"><Sparkles size={12}/> Bijna klaar</div>
              <div className="text-2xl md:text-3xl font-black mt-1 text-slate-900" data-testid="paywall-headline">{paywallHeadline}</div>
              <p className="text-slate-800 font-semibold mt-2 max-w-xl leading-relaxed">{paywallBody}</p>
              {badStatus && hasSubscription && (
                <div className="mt-4 flex gap-2 flex-wrap">
                  <button onClick={openPortal} disabled={busy} className="pb-btn-primary flex items-center gap-2 !min-h-[44px]" data-testid="btn-open-portal-paywall">
                    <CreditCard size={18}/> Update betaalmethode
                  </button>
                </div>
              )}
              <div className="mt-3 text-xs font-bold text-slate-600">Veilige checkout via Stripe · iDEAL + automatische SEPA-incasso · maandelijks opzegbaar</div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="pb-heading text-3xl font-black">FACTURATIE</h1>
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Beheer abonnement, betaalmethode en facturen</div>
        </div>
        {hasSubscription && !isPaywalled && (
          <button onClick={openPortal} disabled={busy} className="pb-btn-outline flex items-center gap-2" data-testid="btn-portal">
            <ExternalLink size={16}/> Facturatie beheren
          </button>
        )}
      </div>

      {!isPaywalled && status.status === "trialing" && trialActive && (
        <div className="mt-4 pb-card p-4 bg-orange-50 border-orange-600 flex items-start gap-3 flex-wrap" data-testid="banner-trial-active">
          <Zap className="text-orange-600 mt-1"/>
          <div className="flex-1 min-w-[220px]">
            <div className="pb-heading font-black">Je proefperiode loopt — nog {Math.max(0, Math.ceil((new Date(status.trial_ends_at) - new Date()) / 86400000))} dagen</div>
            <div className="text-sm text-slate-700 font-semibold">
              Je hoeft nu nog niks te doen. Na {new Date(status.trial_ends_at).toLocaleDateString("nl-NL")} kies je een pakket + aantal extra werknemers.
            </div>
          </div>
        </div>
      )}

      {status.cancel_at_period_end && (
        <div className="mt-4 pb-card p-4 bg-slate-100 flex items-start gap-3">
          <TriangleAlert className="text-slate-600 mt-1"/>
          <div className="text-sm font-semibold">
            Je abonnement is opgezegd en loopt af op {status.current_period_end ? new Date(status.current_period_end).toLocaleDateString("nl-NL") : "einde periode"}.
          </div>
        </div>
      )}

      {/* CURRENT PLAN */}
      <div className="mt-6 pb-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500">Huidig abonnement</div>
            <div className="pb-heading text-2xl font-black mt-1">
              {currentPlan ? `Ploegbord ${currentPlan.name}` : "Geen actief abonnement"}
              {currentPlan && <span className="text-slate-500 text-base font-bold"> · {status.period === "year" ? "jaarlijks" : "maandelijks"}</span>}
            </div>
          </div>
          {status.status && (
            <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full text-white ${
              status.status === "active" ? "bg-green-600" : status.status === "trialing" ? "bg-orange-600" : status.status === "past_due" ? "bg-red-600" : "bg-slate-500"
            }`}>{status.status}</span>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-3 mt-5">
          <MetricCard icon={Users} label="Ingeplande gebruikers deze maand" value={status.planned_users_this_month ?? status.active_uitvoerders_this_month} sub={`${status.included_seats} inbegrepen · ${status.seats_billed} extra betaald · Kantoor gratis (${status.office_used}/${status.office_limit})`}/>
          <MetricCard icon={Clock} label="Volgende afrekening" value={status.current_period_end ? new Date(status.current_period_end).toLocaleDateString("nl-NL") : "—"}/>
          <MetricCard icon={CreditCard} label="Deze maand ongeveer" value={status.estimate ? fmtEUR(status.estimate.total / (status.period === "year" ? 12 : 1)) : "—"} sub={status.estimate ? `${fmtEUR(status.estimate.base / (status.period === "year" ? 12 : 1))} basis + ${status.estimate.seats_billed} × ${fmtEUR(status.estimate.seat_unit / (status.period === "year" ? 12 : 1))}` : ""}/>
        </div>

        {hasSubscription && (
          <div className="mt-4 flex gap-2">
            <button onClick={syncSeats} disabled={busy} className="pb-btn-outline text-sm !min-h-[40px]" data-testid="btn-sync-seats">Nu synchroniseren</button>
          </div>
        )}
      </div>

      {/* PLANS */}
      <div className="mt-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="pb-heading text-xl font-black">{hasSubscription ? "Wisselen van pakket" : "Kies een pakket"}</div>
          <div className="inline-flex border-2 border-slate-900 rounded-full p-1 bg-white">
            {[{ k: "month", l: "Maand" }, { k: "year", l: "Jaar · 2 mnd gratis" }].map((o) => (
              <button key={o.k} onClick={() => setPeriod(o.k)} className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest ${period === o.k ? "bg-slate-900 text-white" : ""}`} data-testid={`billing-period-${o.k}`}>{o.l}</button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {plans.tiers.map((p) => {
            const isCurrent = status.tier === p.tier && status.period === period;
            const price = p.prices[period];
            const yearDiv = period === "year" ? 12 : 1;
            const chosen = seatChoice[p.tier] ?? 0;
            const monthlyBase = price.base_amount / yearDiv;
            const monthlySeat = price.seat_amount / yearDiv;
            const monthlyTotal = monthlyBase + chosen * monthlySeat;
            const periodTotal = price.base_amount + chosen * price.seat_amount;
            return (
              <div key={p.tier} className={`pb-card p-5 flex flex-col ${isCurrent ? "ring-4 ring-orange-600" : ""}`} data-testid={`plan-card-${p.tier}`}>
                <div className="flex items-center justify-between">
                  <div className="pb-heading text-xl font-black">{p.name}</div>
                  {p.tier === "pro" && <span className="text-[10px] font-black uppercase tracking-widest text-orange-600 border-2 border-orange-600 rounded-full px-2 py-0.5">Populair</span>}
                </div>
                <div className="mt-2">
                  <span className="pb-heading text-4xl font-black tabular-nums">{fmtEUR(monthlyBase)}</span>
                  <span className="text-slate-500 font-bold text-sm"> / mnd</span>
                </div>
                <div className="text-xs font-bold text-slate-500 mt-1">{p.included_seats} werknemers inbegrepen · +{fmtEUR(monthlySeat)}/extra werknemer</div>

                {/* Extra seats picker — actief zolang men NOG geen abonnement heeft (checkout flow) */}
                {!hasSubscription && (
                  <div className="mt-4 border-2 border-dashed border-slate-300 rounded-lg p-3 bg-slate-50">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">Extra werknemers (boven {p.included_seats})</div>
                    <div className="mt-2 flex items-center gap-2">
                      <button type="button" onClick={() => setSeat(p.tier, chosen - 1)} disabled={chosen <= 0} className="w-9 h-9 rounded border-2 border-slate-900 flex items-center justify-center hover:bg-slate-100 disabled:opacity-40" data-testid={`seat-minus-${p.tier}`}>
                        <Minus size={16}/>
                      </button>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={chosen}
                        onChange={(e) => setSeat(p.tier, e.target.value)}
                        className="w-16 h-9 border-2 border-slate-900 rounded text-center font-black tabular-nums"
                        data-testid={`seat-input-${p.tier}`}
                      />
                      <button type="button" onClick={() => setSeat(p.tier, chosen + 1)} className="w-9 h-9 rounded border-2 border-slate-900 flex items-center justify-center hover:bg-slate-100" data-testid={`seat-plus-${p.tier}`}>
                        <Plus size={16}/>
                      </button>
                      <div className="text-xs text-slate-600 font-semibold ml-1">extra {chosen === 1 ? "werknemer" : "werknemers"}</div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-300 flex items-baseline justify-between">
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">Totaal per maand</div>
                      <div className="pb-heading text-2xl font-black tabular-nums text-orange-700" data-testid={`seat-total-${p.tier}`}>{fmtEUR(monthlyTotal)}</div>
                    </div>
                    {period === "year" && (
                      <div className="text-[10px] font-bold text-slate-500 text-right">jaarlijks {fmtEUR(periodTotal)} · 2 mnd gratis</div>
                    )}
                  </div>
                )}

                <div className="mt-4 mt-auto">
                  {isCurrent ? (
                    <div className="pb-btn-outline w-full flex items-center justify-center gap-2 opacity-70 cursor-default"><CheckCircle2 size={16}/> Huidig pakket</div>
                  ) : hasSubscription ? (
                    <button onClick={() => changeTier(p.tier)} disabled={busy} className="pb-btn-primary w-full" data-testid={`btn-switch-${p.tier}`}>Overstappen</button>
                  ) : (
                    <button onClick={() => startCheckout(p.tier)} disabled={busy} className="pb-btn-primary w-full flex items-center justify-center gap-2" data-testid={`btn-start-${p.tier}`}>
                      <CreditCard size={16}/> Betalen met iDEAL
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!hasSubscription && (
          <div className="mt-3 text-xs font-semibold text-slate-500 max-w-2xl">
            Extra werknemers kun je later ook via de facturatie-portal aanpassen — Stripe rekent verrekening (proration) automatisch door.
          </div>
        )}
      </div>

      <div className="mt-6 text-xs font-semibold text-slate-500">
        Betalingen worden verwerkt door Stripe. Wij slaan geen kaartgegevens op. <Link to="/privacy" className="underline">Privacy</Link> · <Link to="/voorwaarden" className="underline">Voorwaarden</Link>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border-2 border-slate-900 rounded p-3">
      <div className="flex items-center gap-2 text-slate-600 text-xs font-black uppercase tracking-widest"><Icon size={14}/>{label}</div>
      <div className="pb-heading font-black text-2xl mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] font-bold text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
